import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/shared/api/queryKeys';
import { deploymentsApi } from '../api/deployments.api';
import { MOCK_DEPLOYMENTS_DATA } from '@/shared/mocks/fixtures/deployments.fixture';

export function useDeploymentsList() {
  return useQuery({
    queryKey: queryKeys.deployments.list({}),
    queryFn: deploymentsApi.list,
    staleTime: 30_000,
    // Seed with fixture data so the list is never empty during MSW SW boot delay.
    // TanStack Query replaces placeholderData the moment the real fetch resolves.
    placeholderData: import.meta.env.DEV ? MOCK_DEPLOYMENTS_DATA : undefined,
    retry: 2,
    retryDelay: 600,
  });
}

export function useDeploymentDetail(id) {
  return useQuery({
    queryKey: queryKeys.deployments.detail(id),
    queryFn: () => deploymentsApi.get(id),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateDeployment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deploymentsApi.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.deployments.all() }),
  });
}

export function useUpdateDeployment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => deploymentsApi.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.deployments.all() });
      qc.invalidateQueries({ queryKey: queryKeys.deployments.detail(id) });
    },
  });
}

export function useDeleteDeployment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deploymentsApi.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.deployments.all() }),
  });
}
